import { t, getLocale } from '@viral-space/shared';
import { attachMobileCloseButton } from './ui-shared';

const COLORS = {
  primary: '#00ff41',
  bg: 'rgba(0,12,0,0.88)',
  dim: 'rgba(0,255,65,0.25)',
  border: 'rgba(0,255,65,0.4)',
};

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 6px ${COLORS.primary}, 0 0 12px rgba(0,255,65,0.3)`;

interface ControlBinding {
  keys: string;
  keysEs?: string;
  keysPt?: string;
  action: string;
  actionEs: string;
  actionPt: string;
}

const CONTROLS: ControlBinding[] = [
  { keys: 'W / S', action: 'THRUST FORWARD / BRAKE', actionEs: 'IMPULSO / FRENO', actionPt: 'IMPULSO / FREIO' },
  { keys: 'A / D', action: 'STRAFE LEFT / RIGHT', actionEs: 'DESPLAZAR IZQ / DER', actionPt: 'LATERAL ESQ / DIR' },
  { keys: 'SPACE', keysEs: 'ESPACIO', keysPt: 'ESPAÇO', action: 'ASCEND', actionEs: 'ASCENDER', actionPt: 'SUBIR' },
  { keys: 'C / CTRL', action: 'DESCEND', actionEs: 'DESCENDER', actionPt: 'DESCER' },
  { keys: 'SHIFT', action: 'BOOST', actionEs: 'TURBO', actionPt: 'TURBO' },
  { keys: 'MOUSE', keysEs: 'RATÓN', keysPt: 'MOUSE', action: 'AIM / LOOK AROUND', actionEs: 'APUNTAR / MIRAR', actionPt: 'MIRAR / OLHAR' },
  { keys: 'LEFT CLICK', keysEs: 'CLIC IZQ', keysPt: 'CLIQUE ESQ', action: 'FIRE WEAPON', actionEs: 'DISPARAR', actionPt: 'ATIRAR' },
  { keys: 'F', action: 'DOCK AT STATION', actionEs: 'ATRACAR EN ESTACIÓN', actionPt: 'ATRACAR NA ESTAÇÃO' },
  { keys: 'I', action: 'INVENTORY · EQUIP WEAPON', actionEs: 'INVENTARIO · EQUIPAR ARMA', actionPt: 'INVENTÁRIO · EQUIPAR ARMA' },
  { keys: 'M', action: 'UNIVERSE MAP', actionEs: 'MAPA DEL UNIVERSO', actionPt: 'MAPA DO UNIVERSO' },
  { keys: 'U', action: 'FRIENDS LIST', actionEs: 'LISTA DE AMIGOS', actionPt: 'LISTA DE AMIGOS' },
  { keys: 'Y', action: 'FRIEND-REQUEST TARGETED PILOT', actionEs: 'SOLICITUD AL PILOTO OBJETIVO', actionPt: 'PEDIDO AO PILOTO MIRADO' },
  { keys: 'ENTER', action: 'OPEN CHAT', actionEs: 'ABRIR CHAT', actionPt: 'ABRIR CHAT' },
  { keys: 'ESC', action: 'OPTIONS MENU', actionEs: 'MENÚ DE OPCIONES', actionPt: 'MENU DE OPÇÕES' },
  { keys: 'H', action: 'TOGGLE THIS HELP', actionEs: 'MOSTRAR/OCULTAR AYUDA', actionPt: 'MOSTRAR/OCULTAR AJUDA' },
];

export class ControlsHelp {
  private container: HTMLElement;
  private root: HTMLElement;
  private visible = false;
  private autoHideTimer: ReturnType<typeof setTimeout> | null = null;
  private hintEl: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;

    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: '200',
      background: COLORS.bg,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '2px',
      padding: 'clamp(16px, 3vw, 32px)',
      fontFamily: FONT,
      color: COLORS.primary,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      fontSize: 'clamp(10px, 1.4vw, 13px)',
      boxShadow: `0 0 30px rgba(0,255,65,0.15), inset 0 0 30px rgba(0,255,65,0.03)`,
      display: 'none',
      pointerEvents: 'auto' as string,
      maxWidth: '500px',
      width: '90vw',
      opacity: '0',
      transition: 'opacity 0.3s ease',
    });
    container.appendChild(this.root);

    attachMobileCloseButton(this.root, () => this.hide());

    this.hintEl = document.createElement('div');
    Object.assign(this.hintEl.style, {
      position: 'absolute',
      top: 'clamp(60px, 10vh, 100px)',
      left: '50%',
      transform: 'translateX(-50%)',
      fontFamily: FONT,
      color: COLORS.primary,
      textTransform: 'uppercase',
      letterSpacing: '2px',
      fontSize: 'clamp(11px, 1.5vw, 14px)',
      textShadow: GLOW,
      opacity: '0.8',
      pointerEvents: 'none',
      zIndex: '150',
      whiteSpace: 'nowrap',
      transition: 'opacity 0.5s ease',
    });
    container.appendChild(this.hintEl);

    this.buildContent();

    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyH') {
        this.toggle();
      }
    });
  }

  private buildContent(): void {
    const locale = getLocale();
    const isEs = locale === 'es';
    const isPt = locale === 'pt';

    this.root.innerHTML = '';

    const title = document.createElement('div');
    Object.assign(title.style, {
      textAlign: 'center',
      fontSize: 'clamp(14px, 2vw, 18px)',
      marginBottom: '16px',
      textShadow: GLOW,
      letterSpacing: '4px',
    });
    title.textContent = t('controls.helpTitle');
    this.root.appendChild(title);

    const grid = document.createElement('div');
    Object.assign(grid.style, {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '6px 20px',
    });

    for (const ctrl of CONTROLS) {
      const keyEl = document.createElement('div');
      Object.assign(keyEl.style, {
        textAlign: 'right',
        color: '#ffb000',
        textShadow: '0 0 4px rgba(255,176,0,0.4)',
      });
      keyEl.textContent =
        isEs && ctrl.keysEs ? ctrl.keysEs
        : isPt && ctrl.keysPt ? ctrl.keysPt
        : ctrl.keys;

      const actionEl = document.createElement('div');
      Object.assign(actionEl.style, {
        textAlign: 'left',
        color: COLORS.primary,
        opacity: '0.85',
      });
      actionEl.textContent =
        isEs ? ctrl.actionEs
        : isPt ? ctrl.actionPt
        : ctrl.action;

      grid.appendChild(keyEl);
      grid.appendChild(actionEl);
    }

    this.root.appendChild(grid);

    const separator = document.createElement('div');
    Object.assign(separator.style, {
      height: '1px',
      background: COLORS.dim,
      margin: '14px 0',
    });
    this.root.appendChild(separator);

    const touchNote = document.createElement('div');
    Object.assign(touchNote.style, {
      textAlign: 'center',
      fontSize: '10px',
      opacity: '0.5',
    });
    touchNote.textContent = t('controls.helpMobile');
    this.root.appendChild(touchNote);

    const closeHint = document.createElement('div');
    Object.assign(closeHint.style, {
      textAlign: 'center',
      fontSize: '10px',
      marginTop: '12px',
      opacity: '0.4',
    });
    closeHint.textContent = t('controls.helpClose');
    this.root.appendChild(closeHint);

    this.root.addEventListener('click', () => this.hide());
  }

  isVisible(): boolean {
    return this.visible;
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.buildContent();
    this.root.style.display = 'block';
    requestAnimationFrame(() => {
      this.root.style.opacity = '1';
    });
    this.hideHint();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.root.style.opacity = '0';
    setTimeout(() => {
      if (!this.visible) this.root.style.display = 'none';
    }, 300);
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  showWithAutoHide(durationMs = 8000): void {
    this.show();
    if (this.autoHideTimer) clearTimeout(this.autoHideTimer);
    this.autoHideTimer = setTimeout(() => {
      this.hide();
    }, durationMs);
  }

  showHint(): void {
    this.hintEl.textContent = t('controls.helpHint');
    this.hintEl.style.opacity = '0.8';

    setTimeout(() => {
      this.hintEl.style.opacity = '0';
    }, 6000);
  }

  hideHint(): void {
    this.hintEl.style.opacity = '0';
  }

  dispose(): void {
    if (this.autoHideTimer) clearTimeout(this.autoHideTimer);
    this.root.remove();
    this.hintEl.remove();
  }
}
